/**
 * `/admin/system-fixtures/:id` — single-fixture detail view (#914).
 *
 * A system fixture is only a spec template + metadata; there's no
 * planning/script split like workspace test cases. So this page is a
 * flat stack of sections rather than the two-pane Prompts detail
 * layout — no version history, no comments, no edit controls. The
 * single load-bearing affordance is the `specTemplate` viewer.
 *
 * Sections (in order, matching the issue spec):
 *   1. Breadcrumb + header (id, name, version pill)
 *   2. Description
 *   3. URL patterns — substrings the planner's `match_library_fixture`
 *      MCP tool tests against the URL the planner agent encountered.
 *   4. Required credential fields — the env-var family the fixture
 *      expects via `composeCredentialEnv` (AUTH_<ROLE>_<FIELD>).
 *   5. Lockout signals (only rendered when the fixture defines them).
 *   6. Spec template — full Playwright TypeScript, viewed in the
 *      read-only Monaco pane we already use on the prompt detail
 *      page (script-history pattern). Includes a "Copy" affordance.
 *
 * Code-pane choice: `MonacoCodePane` (same component the Prompts
 * detail page renders). Adding `prism-react-renderer` or another
 * highlighter would be net-new dependency surface; Monaco is already
 * bundled and already proves the dark/light theme + TypeScript
 * highlighting we need on this admin surface. The pane is read-only
 * — these specs are code-defined and reconciled by the boot seeder;
 * editing is intentionally out of scope (the Level 2/3 follow-ups
 * called out in #914 are separate initiatives).
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui'
import {
  adminSystemFixturesAPI,
  type AdminSystemFixtureDetail,
} from '@/lib/api'
import { MonacoCodePane } from '@/pages/Tests/components/MonacoCodePane'

export function SystemFixtureDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<AdminSystemFixtureDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')
  // Track the pending "fade the Copy banner back to idle" timer so
  // we can clear it on unmount or before scheduling a new one. Without
  // this, a quick Copy → unmount sequence triggers a setState-after-
  // unmount, and back-to-back Copy clicks let an older timer overwrite
  // newer banner state (Copilot review on #915).
  const copyTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!id) return
    let alive = true
    setLoading(true)
    void adminSystemFixturesAPI
      .get(id)
      .then((d) => {
        if (!alive) return
        setData(d)
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (!alive) return
        setLoadError(e instanceof Error ? e.message : 'Failed to load fixture')
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [id])

  // Unmount cleanup for the copy-banner reset timer. Separate effect
  // from the data load above so its only dependency is the ref —
  // bundling them would force the cleanup to run on every id change.
  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) {
        window.clearTimeout(copyTimerRef.current)
        copyTimerRef.current = null
      }
    }
  }, [])

  async function handleCopySpec(): Promise<void> {
    if (!data) return
    // Clear any in-flight reset timer FIRST so a rapid second click
    // doesn't get its newer banner state stomped by the older timer.
    if (copyTimerRef.current !== null) {
      window.clearTimeout(copyTimerRef.current)
      copyTimerRef.current = null
    }
    try {
      // Defensive guard: navigator.clipboard is absent in older test
      // environments (jsdom doesn't define it). Surface the error
      // through the UI instead of throwing into an unhandled promise.
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        throw new Error('Clipboard API unavailable in this environment.')
      }
      await navigator.clipboard.writeText(data.specTemplate)
      setCopyState('copied')
      copyTimerRef.current = window.setTimeout(() => {
        setCopyState('idle')
        copyTimerRef.current = null
      }, 2000)
    } catch {
      setCopyState('error')
      copyTimerRef.current = window.setTimeout(() => {
        setCopyState('idle')
        copyTimerRef.current = null
      }, 3000)
    }
  }

  if (loading && !data) {
    return <p className="text-sm text-muted-foreground">Loading fixture…</p>
  }
  if (loadError && !data) {
    return <p className="text-sm text-destructive" role="alert">{loadError}</p>
  }
  if (!data) return null

  return (
    <div className="space-y-6 max-w-full">
      <div className="text-xs text-muted-foreground">
        <Link to="/admin" className="hover:text-foreground">Global Admin</Link>
        {' · '}
        <Link to="/admin/system-fixtures" className="hover:text-foreground">System Fixtures</Link>
        {' · '}
        <span className="font-mono">{data.id}</span>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold font-mono flex items-center gap-2 flex-wrap">
            {data.id}
            <span className="text-xs font-sans font-semibold px-2 py-0.5 rounded bg-muted text-muted-foreground">
              v{data.version}
            </span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{data.name}</p>
        </div>
      </div>

      {data.description && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{data.description}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">URL patterns</CardTitle>
          </CardHeader>
          <CardContent>
            {data.urlPatterns.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No URL patterns — this fixture is not auto-matched against the variant's URL.
              </p>
            ) : (
              <ul className="space-y-1">
                {data.urlPatterns.map((p) => (
                  <li key={p} className="font-mono text-xs px-2 py-1 rounded bg-muted/40">
                    {p}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">
              The planner agent's URL-match tool tests the variant's base URL against these substrings.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Required credential fields</CardTitle>
          </CardHeader>
          <CardContent>
            {data.requiredFields.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No credential fields — this fixture runs without per-role environment.
              </p>
            ) : (
              <ul className="flex flex-wrap gap-1">
                {data.requiredFields.map((f) => (
                  <li key={f} className="font-mono text-[11px] px-2 py-0.5 rounded bg-muted">
                    {f}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">
              The fixture reads these as <code className="font-mono text-[10px]">AUTH_&lt;ROLE&gt;_&lt;FIELD&gt;</code> at run time.
            </p>
          </CardContent>
        </Card>
      </div>

      {data.lockoutSignals && (data.lockoutSignals.domPattern || (data.lockoutSignals.statusCodes && data.lockoutSignals.statusCodes.length > 0)) && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Lockout signals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {data.lockoutSignals.domPattern && (
              <div>
                <span className="text-xs text-muted-foreground mr-2">DOM pattern:</span>
                <code className="font-mono text-xs px-2 py-0.5 rounded bg-muted">{data.lockoutSignals.domPattern}</code>
              </div>
            )}
            {data.lockoutSignals.statusCodes && data.lockoutSignals.statusCodes.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground mr-2">HTTP statuses:</span>
                {data.lockoutSignals.statusCodes.map((s) => (
                  <code key={s} className="font-mono text-xs px-2 py-0.5 rounded bg-muted mr-1">{s}</code>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              When the healer sees one of these signals after a failed login attempt, it stops retrying for the run instead of looping the lockout.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="py-3 flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-sm">Spec template</CardTitle>
          <div className="flex items-center gap-2">
            <span
              role="status"
              aria-live="polite"
              className={`text-xs ${copyState === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {copyState === 'copied'
                ? 'Copied'
                : copyState === 'error'
                  ? 'Copy failed'
                  : ''}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleCopySpec()}
            >
              Copy
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-[11px] text-muted-foreground">
            The <code className="font-mono">{'{{ROLE}}'}</code> / <code className="font-mono">{'{{ROLE_UPPER}}'}</code> placeholders below are substituted at the time a variant adopts this fixture.
          </p>
          {/* Fixed-height pane so the Monaco viewer renders inside a
              scrollable region instead of pushing the rest of the page
              off-screen. Matches the prompt-detail pane's "embedded
              code viewer" sizing convention (480px there too). */}
          <div className="border rounded-md overflow-hidden h-[480px]">
            <MonacoCodePane
              value={data.specTemplate}
              readOnly
              language="typescript"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
